import React, { createContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  signIn,
  signUp,
  signOut,
  updateProfile,
  submitVerification,
  silentRefresh,
  getProfile,
} from '../api/auth';
import { tokenManager } from '../../../shared/api/tokenManager';
import { logError } from '../../../shared/utils/errorUtils';
import type { User, UserFormData, VerificationFormData } from '../types/user';
import { LoadingState as SharedLoadingState } from '../../../shared/components/ui/LoadingState';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (
    email: string,
    password: string,
    rememberMe?: boolean
  ) => Promise<void>;
  logout: () => Promise<void>;
  signUp: (data: UserFormData, queryParams?: string) => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<User>;
  submitVerification: (data: VerificationFormData) => Promise<User>;
  silentRefresh: () => Promise<User | null>;
  redirectToLogin: (message?: string, returnUrl?: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isAuthInitialized, setIsAuthInitialized] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Initialize auth only once on mount
  useEffect(() => {
    initAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const initAuth = async () => {
    try {
      // On initial load, always attempt a silent refresh.
      // The browser will automatically send the HttpOnly refresh token cookie.
      // This will restore the session if the user has a valid refresh token.
      const refreshedUser = await handleSilentRefresh();
      if (refreshedUser) {
        setUser(refreshedUser);
      } else {
        // If refresh fails, ensure no stale tokens are left.
        tokenManager.clearTokens();
        setUser(null);
      }
    } catch {
      // Silent auth init failure is expected when no session exists
      tokenManager.clearTokens();
      setUser(null);
    } finally {
      setLoading(false);
      setIsAuthInitialized(true);
    }
  };

  const handleSilentRefresh = async (): Promise<User | null> => {
    try {
      console.log('🔄 Starting silent token refresh...');

      // Step 1: Get new access token (refresh endpoint only returns token + expiry)
      const response = await silentRefresh();
      if (!response || !response.access_token) {
        console.warn('❌ Silent refresh failed, no access token returned');
        return null;
      }

      console.log('✅ Token refresh successful, fetching user data...');

      // Step 2: Update token in tokenManager (preserve existing user data)
      tokenManager.updateToken(response.access_token, response.expires_in);

      // Step 3: Fetch fresh user data using the new token
      try {
        const userData = await getProfile();
        console.log('✅ User data fetched successfully after token refresh');
        console.log('Silent refresh - User object:', userData);
        
        // Check if userData exists before accessing its properties
        if (userData && userData.id) {
          console.log('Silent refresh - User type field:', userData.type);
          console.log(
            'Silent refresh - User operator_type field:',
            userData.operator_type
          );

          // Update tokenManager with fresh user data
          tokenManager.setTokens(
            response.access_token,
            userData,
            response.expires_in
          );
          return userData;
        } else {
          console.warn('⚠️ User data is undefined after token refresh');
          // Return existing user data if available
          const existingUser = tokenManager.user;
          if (existingUser) {
            console.log('🔄 Using existing user data after undefined user fetch');
            return existingUser;
          }
          return null;
        }
      } catch (userError) {
        console.error(
          '⚠️ Failed to fetch user data after token refresh:',
          userError
        );
        // Even if user fetch fails, we still have a valid token
        // Return the existing user data if available
        const existingUser = tokenManager.user;
        if (existingUser) {
          console.log('🔄 Using existing user data after failed user fetch');
        }
        return existingUser;
      }
    } catch {
      // Token refresh failure is expected when no session exists
      tokenManager.clearTokens();
      return null;
    }
  };

  const login = async (
    email: string,
    password: string,
    rememberMe: boolean = false
  ) => {
    try {
      // Clear any existing tokens before attempting login
      tokenManager.clearTokens();
      const response = await signIn(email, password, rememberMe);

      // Debug logging to see the actual user object structure
      console.log('Login - Full response:', response);
      
      // Check if user object exists before accessing its properties
      if (response.user && response.user.id) {
        console.log('Login - User type field:', response.user.type);
        console.log(
          'Login - User operator_type field:',
          response.user.operator_type
        );

        // Backend returns refresh_expires_in; use it as token lifetime reference
        // Default to 300s (5 min) for access token TTL
        const expiresIn = response.refresh_expires_in || 300;
        tokenManager.setTokens(
          response.access_token,
          response.user,
          expiresIn
        );
        setUser(response.user);
      } else {
        console.error('❌ Login response contains no user data');
        throw new Error('Login failed: No user data received');
      }

      // Handle redirect after successful login
      navigate('/dashboard', { replace: true });
    } catch (error) {
      tokenManager.clearTokens();
      setUser(null);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut();
    } finally {
      tokenManager.clearTokens();
      setUser(null);
      // Redirect to home and refresh to clear in-memory access token
      navigate('/', { replace: true });
      window.location.reload();
    }
  };

  const signUpUser = async (data: UserFormData, queryParams?: string) => {
    try {
      await signUp(data, queryParams);
    } catch (error) {
      logError(error, 'signUp');
      throw error;
    }
  };

  const updateUserProfile = async (data: Partial<User>) => {
    try {
      const updated = await updateProfile(data);
      setUser(prev => (prev ? { ...prev, ...updated } : updated));
      return updated;
    } catch (error) {
      logError(error, 'updateProfile');
      throw error;
    }
  };

  const verifyUser = async (data: VerificationFormData) => {
    try {
      const updated = await submitVerification(data);
      setUser(updated);
      return updated;
    } catch (error) {
      logError(error, 'submitVerification');
      throw error;
    }
  };

  const redirectToLogin = (message?: string, returnUrl?: string) => {
    if (loading || (user && tokenManager.accessToken)) {
      return;
    }
    navigate('/login', { state: { message, from: returnUrl || location.pathname } });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: Boolean(user && tokenManager.accessToken && !loading),
        loading,
        login,
        logout,
        signUp: signUpUser,
        updateProfile: updateUserProfile,
        submitVerification: verifyUser,
        silentRefresh: handleSilentRefresh,
        redirectToLogin,
      }}
    >
      {isAuthInitialized ? children : <AuthLoadingScreen />}
    </AuthContext.Provider>
  );
};

export const AuthLoadingScreen: React.FC = () => {
  return <SharedLoadingState type="auth" />;
};

export type { AuthContextType };
export { AuthContext };
